import { ChevronLeftIcon, NumberedListIcon } from '@heroicons/react/24/outline';
import {
  CloseModalButton,
  CopyToClipboardButton,
  IconButton
} from '@library/components/buttons';
import type {
  FormattedNewWagerAttributes,
  FormattedNewWagerLineup
} from '@library/types/newWagers';
import { useCallback, useEffect, useMemo } from 'react';
import { classNames } from '@library/utils';
import { formattedNewWagerDetails } from '@bo-utils/newWagers';
import { useSearchParams } from 'react-router-dom';

export interface MemberEntryModalHeaderProps {
  isArenaTableContentVisible: boolean;
  lineup: FormattedNewWagerLineup;
  newWager: FormattedNewWagerAttributes;
  onCancelModal: () => void;
  onShowArenaTableContent: (value: boolean) => void;
}

export const MemberEntryModalHeader = ({
  isArenaTableContentVisible,
  lineup,
  newWager,
  onCancelModal,
  onShowArenaTableContent
}: MemberEntryModalHeaderProps) => {
  const [searchParams] = useSearchParams();
  const modalContent = searchParams.get('content');
  useEffect(() => {
    if (modalContent === 'arenaTable') {
      onShowArenaTableContent(true);
    }
  }, [modalContent, onShowArenaTableContent]);

  // Not a toggle since these functions belong to two different buttons
  const handleShowArenaTable = useCallback(() => {
    onShowArenaTableContent(true);
  }, [onShowArenaTableContent]);

  const handleHideArenaTable = useCallback(() => {
    onShowArenaTableContent(false);
  }, [onShowArenaTableContent]);

  const { amountBet, newWagerIdUrl, resultHeader } = useMemo(
    () => formattedNewWagerDetails(newWager, lineup),
    [newWager, lineup]
  );
  const arenaTableEntryModalUrl = `${newWagerIdUrl}&content=arenaTable`;
  const { contestId, lineupGameType } = newWager;
  return (
    <div
      className="border-soFresh-140 flex items-center border-b px-6 py-4"
      data-testid="member-entry-modal-header"
    >
      {isArenaTableContentVisible && (
        <IconButton className="mr-3" handleOnClick={handleHideArenaTable}>
          <ChevronLeftIcon
            className="text-soClean-100 hover:bg-soFresh-120 size-9 items-center rounded-md p-1"
            data-testid="ChevronLeftIcon"
          />
        </IconButton>
      )}
      <div className="flex h-10 flex-col justify-between">
        <p className="text-soClean-100 font-semibold leading-none">
          <span>{resultHeader.text}</span>
          <span className={classNames('pl-1', resultHeader.color)}>
            {resultHeader.resultAmount}
          </span>
        </p>
        <p className="text-soClean-180 text-sm capitalize leading-none">
          {amountBet} {lineupGameType}
        </p>
      </div>
      <div className="ml-auto flex items-center">
        {!!contestId && !isArenaTableContentVisible && (
          <IconButton handleOnClick={handleShowArenaTable} className="m-0">
            <NumberedListIcon
              className="text-soClean-180 hover:text-soClean-100 hover:bg-soFresh-120 size-10 items-center rounded-md p-2"
              data-testid="NumberedListIcon"
            />
          </IconButton>
        )}
        <CopyToClipboardButton
          iconClassName="text-soClean-180 group-hover:text-soClean-100"
          iconSize={5}
          bannerMessage="Copied Entry URL to Clipboard"
          btnClassName="size-10 hover:bg-soFresh-120 rounded-md m-0 group hover:inherit"
          textCopied={
            isArenaTableContentVisible ? arenaTableEntryModalUrl : newWagerIdUrl
          }
        />
        <CloseModalButton
          btnClassName="p-0 text-soClean-180 size-10 hover:bg-soFresh-120 rounded-md group hover:inherit"
          handleOnClick={onCancelModal}
          labelClassName="text-soClean-180 group-hover:text-soClean-100"
          useDefaultStyling={false}
        />
      </div>
    </div>
  );
};
